import type { ReactNode } from 'react';
import { Dialog, type DialogCloseReason } from '../../Surfaces/Dialog';
import { Button } from '../../Button';
import { Typography } from '../../Typography';
import type { ConfirmDialogProps } from './types';

const DEFAULT_CANCEL_LABEL = 'Cancel';
const DEFAULT_OK_LABEL = 'OK';

/**
 * Confirmation dialog with optional title, message/content, and actions.
 * Supports default OK-only or custom action + cancel. Use cancelLabel/okLabel for i18n.
 *
 * @example
 * <ConfirmDialog
 *   open={open}
 *   onClose={handleClose}
 *   title="Confirm"
 *   message="Are you sure?"
 *   okLabel={t('ok')}
 *   cancelLabel={t('cancel')}
 * />
 */
export function ConfirmDialog({
  title,
  content,
  message,
  action,
  open,
  isError,
  onClose,
  cancelButtonColor = 'secondary',
  cancelLabel = DEFAULT_CANCEL_LABEL,
  okLabel = DEFAULT_OK_LABEL,
  ...other
}: ConfirmDialogProps) {
  const dialogContent: ReactNode =
    message || content ? (
      <>
        {message && <Typography variant="body2">{message}</Typography>}
        {content}
      </>
    ) : undefined;

  const handleClose = (_event: object, _reason: DialogCloseReason) => {
    onClose?.();
  };

  const dialogActions: ReactNode = action ? (
    <>
      {action}
      {onClose && (
        <Button variant="text" shape="pill" color={cancelButtonColor} onClick={onClose}>
          {cancelLabel}
        </Button>
      )}
    </>
  ) : (
    <Button variant="text" shape="pill" color={isError ? 'error' : 'primary'} onClick={onClose}>
      {okLabel}
    </Button>
  );

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      open={open}
      onClose={handleClose}
      title={title}
      content={dialogContent}
      actions={dialogActions}
      isError={isError}
      {...other}
    />
  );
}
